import React from 'react';

import { FormattedMessage } from 'react-intl-next';

import { LinkButton } from '@atlaskit/button/new';
import Modal, { CloseButton, ModalTitle } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { Anchor, Text } from '@atlaskit/primitives/compiled';
import { UserLimits } from '@atlassian/growth-pattern-library-user-limits';

import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID,
	AVOID_DEACTIVATION_MODAL_TEST_ID,
	MAX_USERS_ON_FREE_PLAN,
} from '../../utils/constants';
import { getChargeElementTypeByProduct } from '../../utils/product-terms';

import type { AutoUpgradeAvoidDeactivationProps } from './types';

export const AutoUpgradeAvoidDeactivationModal = ({
	product,
	touchpointId,
	deactivationDate,
	licensedUsers,
	addPaymentUrl,
	removeUsersUrl,
	onClose,
}: AutoUpgradeAvoidDeactivationProps): React.JSX.Element => {
	const {
		createAddPaymentsButtonClickEvent,
		createRemoveUsersButtonClickEvent,
		sendAnalyticEvent,
	} = useAnalytics();

	const daysLeftUntilDeactivation = deactivationDate && deactivationDate.daysLeft;

	const deactivationDateString = deactivationDate && deactivationDate.dateString;

	const handleOnClose = (trigger: 'blur' | 'dismiss') => {
		sendAnalyticEvent({
			action: trigger,
			actionSubject: 'clicked',
			actionSubjectId: AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID,
			attributes: {
				product,
				trigger,
				daysLeftUntilDeactivation,
				touchpointId,
			},
		});

		onClose();
	};

	const handleAddPaymentClick = () => {
		createAddPaymentsButtonClickEvent(AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID, {
			product,
			touchpointId,
			daysLeftUntilDeactivation,
		});
	};

	const handleRemoveUsersClick = () => {
		createRemoveUsersButtonClickEvent(AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID, {
			product,
			touchpointId,
			daysLeftUntilDeactivation,
		});
	};

	const { chargeElementPlural: userTermPlural } = getChargeElementTypeByProduct(product);
	const licensedUserLimit = MAX_USERS_ON_FREE_PLAN[product] || 10;

	const removeUsersByDeactivationDateAvailable = (
		<FormattedMessage
			id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.description.take-action-by-trial-end-date-available"
			defaultMessage="by {deactivationMonthDay}"
			description="take action by trial end date"
			values={{
				deactivationMonthDay: deactivationDateString,
			}}
		/>
	);

	const removeUsersByDeactivationDateNotAvailable = (
		<FormattedMessage
			id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.description.take-action-by-trial-end-date-not-available"
			defaultMessage="soon"
			description="take action soon"
		/>
	);

	return (
		<Modal
			testId={AVOID_DEACTIVATION_MODAL_TEST_ID}
			onClose={() => handleOnClose('blur')}
			width={600}
			shouldScrollInViewport={false}
			shouldCloseOnEscapePress={true}
			shouldCloseOnOverlayClick={true}
		>
			<Stack alignInline="center" xcss={containerStyleWithPadding}>
				<Stack space="space.300" alignInline="center">
					<Stack space="space.150" alignInline="center">
						<Inline grow="fill" alignBlock="center" alignInline="end">
							<Inline grow="fill" alignInline="center">
								<ModalTitle appearance="warning">
									<FormattedMessage
										id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.header"
										defaultMessage="Take action, avoid deactivation"
										description="Title of a popup modal telling the they need to take action to avoid deactivation of their product"
									/>
								</ModalTitle>
							</Inline>

							<CloseButton
								onClick={() => handleOnClose('dismiss')}
								testId={AVOID_DEACTIVATION_MODAL_TEST_ID}
							/>
						</Inline>

						<Text align="center">
							<FormattedMessage
								id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.description"
								defaultMessage="You’re over the Free limit, so we moved you to Standard, which includes unlimited {userTermPlural, select, users {users} agents {agents} creators {creators} other {users}}. Add payment to keep it or <a>remove users</a> {removeUsersBy}."
								description="Description of a popup modal telling the user they need to take action to avoid deactivation of their product"
								values={{
									removeUsersBy: deactivationDate
										? removeUsersByDeactivationDateAvailable
										: removeUsersByDeactivationDateNotAvailable,
									userTermPlural,
									a: (chunks) => (
										<Anchor href={removeUsersUrl} target="_blank" onClick={handleRemoveUsersClick}>
											{chunks}
										</Anchor>
									),
								}}
							/>
						</Text>
					</Stack>
					<Inline grow="fill" alignInline="center" xcss={userLimitStyles}>
						<Text align="center">
							<UserLimits
								title={
									<FormattedMessage
										id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.user-limits.title"
										defaultMessage="{userTermPlural, select, users {{numberOfUsers} people invited} agents {{numberOfUsers} agents invited} creators {{numberOfUsers} creators invited} other {{numberOfUsers} people invited}}"
										description="How many users have been invited out of the Free plans limit"
										values={{
											numberOfUsers: licensedUsers,
											userTermPlural,
										}}
									/>
								}
								description={
									<Text color="color.text">
										<FormattedMessage
											id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.user-limits.description.near-limit"
											defaultMessage="On Free, you’ll only be able to keep {licensedUserLimit} {userTermPlural, select, users {users} agents {agents} creators {creators} other {users}}."
											description="To inform the user that upgrading would shift from their current limit to unlimited creators"
											values={{
												licensedUserLimit,
												userTermPlural,
											}}
										/>
									</Text>
								}
								assignedUsers={licensedUsers}
								dividerLimit={licensedUserLimit}
								limit={licensedUserLimit}
								size="medium"
								warningStyle
							/>
						</Text>
					</Inline>
					<Stack alignInline="center">
						<LinkButton
							appearance="primary"
							href={addPaymentUrl}
							target="_blank"
							onClick={handleAddPaymentClick}
						>
							<FormattedMessage
								id="@atlassian/auto-upgrade-not-a-trial.avoid-deactivation-modal.upgrade-button"
								defaultMessage="Add payment"
								description="Button text to add payment"
							/>
						</LinkButton>
					</Stack>
					<ScreenEvent
						name={AVOID_DEACTIVATION_MODAL_ACTION_SUBJECT_ID}
						attributes={{ product, touchpointId, daysLeftUntilDeactivation }}
					/>
				</Stack>
			</Stack>
		</Modal>
	);
};

const containerStyleWithPadding = xcss({
	padding: 'space.500',
});

const userLimitStyles = xcss({
	backgroundColor: 'color.background.input.hovered',
	padding: 'space.300',
});
